import { useEffect, useRef, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

// Singleton: ensures only one ad shows at a time across the app
let _adShowing = false;

/**
 * Sets up periodic + page-leave triggers globally. Mail-sent trigger is
 * driven by a custom DOM event: window.dispatchEvent(new Event('omni:ad:mail_sent')).
 *
 * Mounts the actual fullscreen ad overlay only when an ad is active.
 */
export function AdManager() {
  const { user } = useAuth() || {};
  const location = useLocation();
  const [activeAd, setActiveAd] = useState(null); // { id, name, mime, stream_url, skip_after_seconds }
  const [skipIn, setSkipIn] = useState(0);
  const intervalRef = useRef(null);
  const minuteRef = useRef(null);
  const videoRef = useRef(null);
  const lastMinuteFetchRef = useRef(0);

  const fetchAd = useCallback(async (trigger) => {
    if (!user || user === false) return null;
    if (_adShowing) return null;
    try {
      const { data } = await api.get("/ads/random", { params: { trigger } });
      return data.ad;
    } catch { return null; }
  }, [user]);

  const showAd = useCallback(async (trigger) => {
    const ad = await fetchAd(trigger);
    if (!ad) return;
    _adShowing = true;
    setActiveAd(ad);
    setSkipIn(ad.skip_after_seconds || 5);
  }, [fetchAd]);

  const closeAd = useCallback(() => {
    setActiveAd(null);
    setSkipIn(0);
    _adShowing = false;
  }, []);

  // Trigger 1 — mail_sent (custom event)
  useEffect(() => {
    if (!user || user === false) return;
    const onMail = () => showAd("mail_sent");
    window.addEventListener("omni:ad:mail_sent", onMail);
    return () => window.removeEventListener("omni:ad:mail_sent", onMail);
  }, [showAd, user]);

  // Trigger 2 — page_leave (best-effort; fires only when ad is not yet shown)
  useEffect(() => {
    if (!user || user === false) return;
    const onLeave = () => {
      if (document.visibilityState === "hidden" && !_adShowing) {
        // Lazy load (we can't await on unload, so trigger now & let it appear when user returns)
        showAd("page_leave");
      }
    };
    const onBeforeUnload = () => {
      // Pre-warm: fetch now so when user returns it can be shown if they navigate
      showAd("page_leave");
    };
    document.addEventListener("visibilitychange", onLeave);
    window.addEventListener("pagehide", onBeforeUnload);
    return () => {
      document.removeEventListener("visibilitychange", onLeave);
      window.removeEventListener("pagehide", onBeforeUnload);
    };
  }, [showAd, user]);

  // Trigger 3 — every minute, while user is on a regular app page
  useEffect(() => {
    if (!user || user === false) return;
    const blockedPaths = ["/login", "/register", "/forgot-password"];
    if (blockedPaths.some((p) => location.pathname.startsWith(p))) return;
    if (minuteRef.current) clearInterval(minuteRef.current);
    minuteRef.current = setInterval(() => {
      const now = Date.now();
      if (now - lastMinuteFetchRef.current < 55000) return; // safety
      lastMinuteFetchRef.current = now;
      showAd("every_minute");
    }, 60_000);
    return () => minuteRef.current && clearInterval(minuteRef.current);
  }, [showAd, user, location.pathname]);

  // Skip-after countdown
  useEffect(() => {
    if (!activeAd) return;
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setSkipIn((s) => Math.max(0, s - 1));
    }, 1000);
    return () => intervalRef.current && clearInterval(intervalRef.current);
  }, [activeAd]);

  if (!activeAd) return null;

  const canSkip = skipIn <= 0;
  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 99999,
        background: "rgba(0,0,0,.92)", display: "flex",
        alignItems: "center", justifyContent: "center", padding: 16,
      }}
      data-testid="ad-overlay"
    >
      <div style={{ position: "relative", width: "100%", maxWidth: 900, aspectRatio: "16/9", background: "#000", borderRadius: 12, overflow: "hidden" }}>
        <video
          ref={videoRef}
          src={`${API}${activeAd.stream_url.replace(/^\/api/, "")}`}
          autoPlay
          playsInline
          controls
          onEnded={closeAd}
          onError={closeAd}
          style={{ width: "100%", height: "100%", background: "#000" }}
          data-testid="ad-video"
        />
        <div style={{ position: "absolute", top: 12, left: 12, color: "#fff", background: "rgba(0,0,0,.6)", padding: "4px 10px", borderRadius: 999, fontSize: 11, fontFamily: "system-ui" }}>
          Publicité — {activeAd.name}
        </div>
        <button
          onClick={canSkip ? closeAd : undefined}
          disabled={!canSkip}
          data-testid="ad-skip-btn"
          style={{
            position: "absolute", bottom: 16, right: 16,
            padding: "8px 16px", borderRadius: 999,
            background: canSkip ? "#ff4500" : "rgba(255,255,255,.18)",
            color: "#fff", border: "none", cursor: canSkip ? "pointer" : "not-allowed",
            fontSize: 13, fontWeight: 600, fontFamily: "system-ui",
          }}
        >
          {canSkip ? "Passer ✕" : `Passer dans ${skipIn}s`}
        </button>
      </div>
    </div>
  );
}
